import { skyEncounterBeat } from './88-sky-encounter-beat.mjs';
import { skyBossDesign } from './85-sky-boss-designs.mjs';

/** Authored combat descriptors. All distances are metres, speeds metres/second,
 * times active seconds. These deterministic samples own no clock or renderer. */
const classes = {
  scout: {
    size: 0.85,
    radius: 0.85,
    hp: 2,
    speedScale: 0.9,
    agility: 0.7,
    aggression: 1,
    attackPattern: 'aimed',
    value: 100,
  },
  flanker: {
    size: 0.8,
    radius: 0.8,
    hp: 2,
    speedScale: 1.08,
    agility: 1.1,
    aggression: 2,
    attackPattern: 'paired',
    value: 130,
  },
  bulwark: {
    size: 1.45,
    radius: 1.35,
    hp: 5,
    speedScale: 0.72,
    agility: 0.35,
    aggression: 2,
    attackPattern: 'fan',
    value: 180,
  },
  interceptor: {
    size: 0.7,
    radius: 0.7,
    hp: 2,
    speedScale: 1.22,
    agility: 1.35,
    aggression: 1,
    attackPattern: 'aimed',
    value: 160,
  },
  weaver: {
    size: 1.05,
    radius: 1,
    hp: 3,
    speedScale: 0.92,
    agility: 1.2,
    aggression: 2,
    attackPattern: 'paired',
    value: 170,
  },
  ace: {
    size: 1.15,
    radius: 1.1,
    hp: 4,
    speedScale: 0.92,
    agility: 0.95,
    aggression: 3,
    attackPattern: 'fan',
    value: 220,
  },
};
export const SKY_ENEMY_CLASSES = Object.freeze(
  Object.fromEntries(Object.entries(classes).map(([key, value]) => [key, Object.freeze(value)])),
);
const chapters = [
  ['SCOUT SCREEN', 'scout', 'wedge', 'sweep'],
  ['CROSSWIND AMBUSH', 'flanker', 'staggered', 'rising-arc'],
  ['ARMORED CONVOY', 'bulwark', 'diamond', 'sweep'],
  ['INTERCEPTOR SPRINT', 'interceptor', 'line', 'weave'],
  ['SPIRAL HUNT', 'weaver', 'diamond', 'weave'],
  ['ACE VANGUARD', 'ace', 'wedge', 'rising-arc'],
];
export const SKY_CHAPTER_SECONDS = 6;
export const SKY_CHAPTERS = Object.freeze(
  chapters.map(([name, enemyClass, formation, flightPath], index) =>
    Object.freeze({ index, name, enemyClass, formation, flightPath }),
  ),
);
export const SKY_FORMATIONS = Object.freeze({
  wedge: Object.freeze(
    [
      [-2.7, -0.6, -9],
      [-0.9, 0.6, -3],
      [0.9, 0.6, -3],
      [2.7, -0.6, -9],
    ].map(Object.freeze),
  ),
  line: Object.freeze(
    [
      [-2.7, 0, 0],
      [-0.9, 0, 0],
      [0.9, 0, 0],
      [2.7, 0, 0],
    ].map(Object.freeze),
  ),
  staggered: Object.freeze(
    [
      [-2.4, -1.2, 0],
      [-0.8, -0.4, -4],
      [0.8, 0.4, -8],
      [2.4, 1.2, -12],
    ].map(Object.freeze),
  ),
  diamond: Object.freeze(
    [
      [0, 1.4, 0],
      [-2.1, 0, -5],
      [2.1, 0, -5],
      [0, -1.4, -10],
    ].map(Object.freeze),
  ),
});
// Biomes remix learned roles into different approach problems without raising
// projectile speed. Forest remains the six-role tutorial used by stage one.
const biomeOrders = {
  forest: [0, 1, 2, 3, 4, 5],
  ruins: [0, 2, 4, 1, 3, 5],
  fungi: [4, 0, 1, 3, 2, 5],
  tunnel: [3, 2, 0, 4, 1, 5],
  ice: [1, 3, 4, 2, 0, 5],
  void: [4, 1, 3, 0, 2, 5],
  citadel: [2, 3, 1, 4, 0, 5],
  'golden-industrial': [0, 3, 2, 1, 4, 5],
};
const industrialChapters = Object.freeze([
  'HARBOR WATCH',
  'REFINERY INTERCEPTORS',
  'ARMORED FREIGHT ESCORT',
  'CROSSWIND PATROL',
  'TURBINE SCREEN',
  'GILDED ACE GUARD',
]);
const biomeChapters = Object.freeze(
  Object.fromEntries(
    Object.entries(biomeOrders).map(([biome, order], biomeIndex) => [
      biome,
      Object.freeze(
        order.map((role, index) =>
          Object.freeze({
            ...SKY_CHAPTERS[role],
            index,
            ...(biome === 'golden-industrial' ? { name: industrialChapters[index] } : {}),
            formation:
              biome === 'forest'
                ? SKY_CHAPTERS[role].formation
                : ['wedge', 'staggered', 'diamond', 'line'][(index + biomeIndex) % 4],
            flightPath:
              biome === 'forest'
                ? SKY_CHAPTERS[role].flightPath
                : ['sweep', 'rising-arc', 'weave'][(index + biomeIndex) % 3],
          }),
        ),
      ),
    ]),
  ),
);
/** Return a biome-authored approach chapter. Invalid time/biome resolves to the
 * first forest chapter; the last three approach seconds allow a readable respite. */
export function skyChapter(activeTime, biome = 'forest') {
  const time = Number.isFinite(activeTime) ? Math.max(0, activeTime) : 0;
  const sequence = biomeChapters[biome] ?? biomeChapters.forest;
  return sequence[Math.min(5, Math.floor(time / SKY_CHAPTER_SECONDS))];
}
/** Select one finite approach wave by completed-wave index, never elapsed time.
 * The journey controller owns admission/retries; these shared descriptors own
 * the visible role, formation and path. Golden-industrial uses existing armored
 * craft, interceptors and aces, not the whale's objective-bearing dolphins.
 * An invalid index returns null rather than silently repeating the last wave. */
export function skyJourneyChapter(stage, index) {
  if (!Number.isInteger(index) || index < 0 || index >= SKY_CHAPTERS.length) return null;
  return (biomeChapters[stage?.biome] ?? biomeChapters.forest)[index];
}
/** Late approaches pair two escorts with the lead role. All members still share
 * the lead's movement envelope, keeping silhouettes and attack patterns varied
 * without splitting an authored formation or increasing encounter speed. */
export function skySquadClass(chapter, sector, slot) {
  if (sector < 4 || chapter.index < 4 || slot % 2 === 0) return chapter.enemyClass;
  return ['flanker', 'scout', 'interceptor'][(sector + chapter.index) % 3];
}
/** Sample formation-relative flight in metres at active age in seconds. Shared
 * banking preserves the authored silhouette; small slot-specific lag prevents
 * a rigid wall of craft. No leader or previous frame is consulted, so a kill,
 * pause or rewind cannot alter another member's path. Zero age is the spawn pose. */
export function skySquadDisplacement(enemy, activeAge) {
  const age = Number.isFinite(activeAge) ? Math.max(0, activeAge) : 0;
  const agility = Math.max(0.25, Math.min(1.35, enemy.formationAgility ?? enemy.agility ?? 0.7));
  const frequency = (enemy.flightPath === 'weave' ? 0.75 : 0.55) * agility;
  const phase = enemy.phase ?? 0;
  const wave = (rate, amplitude, offset = phase) => ({
    p: (Math.sin(age * rate + offset) - Math.sin(offset)) * amplitude,
    v: Math.cos(age * rate + offset) * rate * amplitude,
    a: -Math.sin(age * rate + offset) * rate * rate * amplitude,
  });
  const u = Math.min(1, age / 1.6);
  const blend = u ** 3 * (10 - 15 * u + 6 * u * u);
  const blendV = age < 1.6 ? (30 * u * u * (1 - u) ** 2) / 1.6 : 0;
  const blendA = age < 1.6 ? (60 * u * (1 - u) * (1 - 2 * u)) / 1.6 ** 2 : 0;
  const orbit = (rate, amplitude, offset, origin, centre) => {
    const angle = age * rate + offset;
    const p = centre + Math.sin(angle) * amplitude - origin;
    const v = Math.cos(angle) * rate * amplitude;
    const a = -Math.sin(angle) * rate * rate * amplitude;
    return { p: p * blend, v: v * blend + p * blendV, a: a * blend + 2 * v * blendV + p * blendA };
  };
  // The entire squad traverses the corridor while retaining its authored slots.
  // Quintic entry joins position/velocity/acceleration without a spawn-time jerk.
  const turn = wave(0.65 * agility, 0.09);
  const lateral = orbit(frequency, 8.2, phase, enemy.centerX ?? 0, 0);
  const slots = SKY_FORMATIONS[enemy.formation] ?? SKY_FORMATIONS.staggered;
  const extent = Math.max(...slots.map(([x, y]) => Math.abs(y) + Math.abs(x) * Math.sin(0.18)));
  // Reserve the banked slot envelope and individual lag before spending height
  // on the shared climb. Wide, flat formations can climb farther than diamonds.
  const climb = Math.min(2.4, 5.1 - 1.4 - extent - 0.09, 9 - 5.1 - extent - 0.09);
  const vertical = orbit(
    0.65 * agility,
    climb,
    phase * 0.7,
    enemy.centerY ?? 4.7,
    5.1 + (enemy.laneOriginY ?? 0),
  );
  const individual = (enemy.slot ?? 0) * 1.7 + phase;
  const lagX = wave(1.2 * agility, 0.07, individual);
  const lagY = wave(1.05 * agility, 0.04, individual);
  const speed = enemy.approachSpeed ?? 17;
  const depth = {
    p: -0.4 * speed * age + Math.sin(age * 0.85) * 25,
    v: -0.4 * speed + Math.cos(age * 0.85) * 21.25,
    a: -Math.sin(age * 0.85) * 18.0625,
  };
  const lagZ = wave(1.1 * agility, 0.08, individual);
  const ox = enemy.offsetX ?? 0,
    oy = enemy.offsetY ?? 0;
  // Members trade left/right slots through a depth braid, then return. The
  // half-cosine clock has zero speed at each end; positive-x members pass behind
  // negative-x members, preserving staggered rows rather than cutting through.
  const angle = (Math.PI / 2) * (1 - Math.cos(age * 0.6));
  const av = (Math.PI / 2) * 0.6 * Math.sin(age * 0.6);
  const aa = (Math.PI / 2) * 0.36 * Math.cos(age * 0.6);
  const bx = ox * Math.cos(angle),
    bv = -ox * Math.sin(angle) * av;
  const ba = -ox * (Math.cos(angle) * av * av + Math.sin(angle) * aa);
  const bz = -ox * Math.sin(angle),
    bzv = -ox * Math.cos(angle) * av;
  const sin = Math.sin(turn.p),
    cos = Math.cos(turn.p);
  const dx = -bx * sin - oy * cos,
    dy = bx * cos - oy * sin;
  return {
    x: lateral.p + bx * cos - ox - oy * sin + lagX.p,
    y: vertical.p + bx * sin + oy * (cos - 1) + lagY.p,
    z: depth.p + lagZ.p + bz,
    vx: lateral.v + bv * cos + dx * turn.v + lagX.v,
    vy: vertical.v + bv * sin + dy * turn.v + lagY.v,
    vz: depth.v + lagZ.v + bzv,
    ax:
      lateral.a +
      ba * cos -
      2 * bv * sin * turn.v +
      (-bx * cos + oy * sin) * turn.v ** 2 +
      dx * turn.a +
      lagX.a,
    ay: vertical.a + ba * sin + 2 * bv * cos * turn.v + dx * turn.v ** 2 + dy * turn.a + lagY.a,
    bank: turn.p,
  };
}
/** A phase-local attack clock guarantees at least 0.9s telegraph on every new
 * damage phase. Projectiles aim only when fired; they never home after launch.
 * Ordinals are monotonic within a phase, including exact cycle boundaries. */
export function skyBossAttack(phase, activeTime, design) {
  const p = Math.max(1, Math.min(3, Math.trunc(phase) || 1));
  return {
    ...skyEncounterBeat(p, activeTime),
    attackPattern: (skyBossDesign(design)?.patterns ?? ['aimed', 'fan', 'gate'])[p - 1],
  };
}
/** Precision formation reward requires every member to be destroyed by aimed
 * cannon fire for an extra mastery award. Every fully destroyed squad pays the
 * base clear reward; escapes never count and a resolved squad cannot pay twice. */
export function skySquadReward(squad) {
  if (
    !squad ||
    squad.rewarded ||
    squad.escaped !== 0 ||
    !Number.isInteger(squad.total) ||
    squad.total < 1 ||
    squad.total > 8 ||
    squad.kills !== squad.total ||
    !Number.isInteger(squad.precision) ||
    squad.precision < 0 ||
    squad.precision > squad.total ||
    !Number.isInteger(squad.chapter) ||
    squad.chapter < 0 ||
    squad.chapter >= SKY_CHAPTERS.length
  )
    return null;
  const perfect = squad.precision === squad.total;
  return {
    score: (300 + squad.chapter * 50) * (perfect ? 2 : 1),
    salvage: 0,
    perfect,
  };
}
